import React, { useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { assets } from '../../assets/assets'
import { Howl } from 'howler';
import { buildApiUrl } from '../../config/api.config';

const clickOption = new Howl({ src: ['/sounds/click.mp3'] });

const Sidebar = ({showAlert}) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(() => localStorage.getItem('sidebarCollapsed') === 'true');

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', newState);
  };
  
  // Initialize from cache immediately to prevent flicker, but ONLY if the ID matches
  const [restaurantData, setRestaurantData] = useState(() => {
    const activeRestId = localStorage.getItem("currentRestaurantId");
    const cachedId = localStorage.getItem("cachedRestaurantId");
    
    if (activeRestId && cachedId === activeRestId) {
      return {
        name: localStorage.getItem("currentRestaurantName") || "TastyFlow Hub",
        logo: localStorage.getItem("currentRestaurantLogo") || "",
        activeFeatures: JSON.parse(localStorage.getItem("activeFeatures") || "[]"),
        createdAt: localStorage.getItem("createdAt") || null,
        licenseExpiryDate: localStorage.getItem("licenseExpiryDate") || null,
        currentPlan: localStorage.getItem("currentPlan") || 'free_trial'
      };
    }
    return { name: "TastyFlow Hub", logo: "", activeFeatures: [], currentPlan: 'free_trial' };
  });

  const hasFeature = (featureName) => {
    // 1. Owners or users coming from Master Panel have full access
    if (userData?.role === 'owner' || localStorage.getItem('fromMaster') === 'true') return true;

    // 2. Check for 30-day Free Trial (ONLY if no specific plan is purchased)
    if (restaurantData.currentPlan === 'free_trial' && restaurantData.createdAt) {
        const createdDate = new Date(restaurantData.createdAt);
        const now = new Date();
        const diffTime = Math.abs(now - createdDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        // During first 30 days, all features are unlocked ONLY for trial accounts
        if (diffDays <= 30) return true;
    }

    // 3. After trial, check license expiry
    if (restaurantData.licenseExpiryDate) {
        if (new Date(restaurantData.licenseExpiryDate) < new Date()) {
            return false; // License expired
        }
    } else {
        return false; // No license date (legacy or error)
    }

    // 4. Finally, check if the specific feature is in the active list
    return restaurantData.activeFeatures.includes(featureName);
  };

  const [userData, setUserData] = useState(() => {
    return JSON.parse(localStorage.getItem("userDetails")) || null;
  });

  const [adminMode, setAdminMode] = useState(() => {
    return localStorage.getItem("adminMode") || 'restaurant';
  });

  const handleModeChange = (mode) => {
    setAdminMode(mode);
    localStorage.setItem("adminMode", mode);
    clickOption.play();
  };

  const fetchRestaurantInfo = async () => {
    try {
      const userDetails = JSON.parse(localStorage.getItem("userDetails"));
      setUserData(userDetails); 
      const selectedRestId = localStorage.getItem("currentRestaurantId");
      const isMaster = userDetails?.role === 'owner' || localStorage.getItem('fromMaster') === 'true';
      let activeRestaurantId = isMaster ? selectedRestId : userDetails?.restaurantId;

      if (activeRestaurantId && typeof activeRestaurantId === 'object') {
          activeRestaurantId = activeRestaurantId._id;
      }

      const response = await axios.get(buildApiUrl(`restaurant/${activeRestaurantId}`));
      if (response.data.success) {
        const data = response.data.restaurant;
        setRestaurantData({ 
            name: data.name, 
            logo: data.logo, 
            activeFeatures: data.activeFeatures || [],
            createdAt: data.createdAt,
            licenseExpiryDate: data.licenseExpiryDate,
            currentPlan: data.currentPlan
        });
        localStorage.setItem("currentRestaurantName", data.name);
        localStorage.setItem("currentRestaurantLogo", data.logo || "");
        localStorage.setItem("activeFeatures", JSON.stringify(data.activeFeatures || []));
        localStorage.setItem("createdAt", data.createdAt);
        localStorage.setItem("licenseExpiryDate", data.licenseExpiryDate || "");
        localStorage.setItem("cachedRestaurantId", activeRestaurantId);
        localStorage.setItem("currentPlan", data.currentPlan || 'free_trial');
      }
    } catch (error) {
      console.error("Error fetching restaurant info:", error);
    }
  };

  useEffect(() => {
    fetchRestaurantInfo();
    window.addEventListener('identityUpdate', fetchRestaurantInfo);
    return () => window.removeEventListener('identityUpdate', fetchRestaurantInfo);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("fromMaster");
    localStorage.removeItem("ownerToken");
    localStorage.removeItem("currentRestaurantLogo");
    localStorage.removeItem("currentRestaurantName");
    localStorage.removeItem("activeFeatures");
    localStorage.removeItem("cachedRestaurantId");
    localStorage.removeItem("currentRestaurantId");
    clickOption.play();
    if (showAlert) showAlert("Logged out successfully", "success");
    navigate("/login");
  };

  return (
    <div className={`tf-sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <button 
        className="tf-sidebar-toggle-btn"
        onClick={toggleSidebar}
        title="Toggle Sidebar"
      >
        <i className={`fa-solid ${isCollapsed ? 'fa-chevron-right' : 'fa-chevron-left'}`}></i>
      </button>

      <div className="tf-sidebar-branding" style={{ padding: '2rem 1.5rem', borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative' }}>
        {restaurantData.logo ? (
          <img src={restaurantData.logo} alt="Logo" style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'var(--tf-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '900', fontSize: '1.2rem', flexShrink: 0 }}>
            {restaurantData.name.charAt(0)}
          </div>
        )}
        <div className="tf-sidebar-branding-text" style={{ overflow: 'hidden' }}>
          <div style={{ fontSize: '0.65rem', color: 'var(--tf-primary)', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '0.2rem' }}>Managing Node</div>
          <div style={{ fontSize: '1.1rem', fontWeight: '800', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {restaurantData.name}
          </div>
        </div>
      </div>

      {(userData?.role !== 'staff' || userData?.staffRole === 'manager') && (
        <div className="tf-mode-toggle-container" style={{ 
          margin: '0 1rem 1rem',
          display: 'flex',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '10px',
          padding: '4px',
          gap: '4px'
        }}>
          <button
            onClick={() => handleModeChange('restaurant')}
            style={{
              flex: 1,
              padding: '0.7rem 0.5rem',
              border: 'none',
              borderRadius: '8px',
              background: adminMode === 'restaurant' ? 'var(--tf-primary)' : 'transparent',
              color: adminMode === 'restaurant' ? 'white' : 'rgba(255,255,255,0.5)',
              fontWeight: '700',
              fontSize: '0.75rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem'
            }}
          >
            <i className="fa-solid fa-utensils"></i>
            <span className="tf-mode-text">Restaurant</span>
          </button>
          <button
            onClick={() => handleModeChange('staff')}
            style={{
              flex: 1,
              padding: '0.7rem 0.5rem',
              border: 'none',
              borderRadius: '8px',
              background: adminMode === 'staff' ? 'var(--tf-primary)' : 'transparent',
              color: adminMode === 'staff' ? 'white' : 'rgba(255,255,255,0.5)',
              fontWeight: '700',
              fontSize: '0.75rem',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem'
            }}
          >
            <i className="fa-solid fa-users-gear"></i>
            <span className="tf-mode-text">Staff</span>
          </button>
        </div>
      )}

      {userData?.role === 'staff' && userData?.staffRole !== 'manager' && (
        <div style={{
          margin: '0 1rem 1rem',
          padding: '1rem',
          background: 'linear-gradient(135deg, #ff4135 0%, #ff6b5b 100%)',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          boxShadow: '0 4px 15px rgba(255, 65, 53, 0.3)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: userData?.profilePhoto ? `url(${userData.profilePhoto}) center/cover` : 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            color: 'white',
            border: '2px solid white'
          }}>
            {!userData?.profilePhoto && '👨‍🍳'}
          </div>
          <div className="tf-staff-mode-text">
            <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.8)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>
              {userData?.staffRole || 'Staff'} Mode
            </div>
            <div style={{ fontSize: '0.95rem', fontWeight: '700', color: 'white' }}>
              {userData?.name}
            </div>
          </div>
        </div>
      )}
      <div className="tf-sidebar-scroll-area">
        <div className="tf-sidebar-options">
          
          {/* RESTAURANT MODE - Food, Invoices, Tables */}
          {(userData?.role !== 'staff' || userData?.staffRole === 'manager') && adminMode === 'restaurant' && (
            <>
              <NavLink to='/admin/add' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                <i className="fa-solid fa-circle-plus"></i>
                <p>Add Items</p>
              </NavLink>
              <NavLink to='/admin/list' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                <i className="fa-solid fa-list-check"></i>
                <p>List Items</p>
              </NavLink>

              {hasFeature('Inventory Management') && (
                <NavLink to='/admin/inventory' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                  <i className="fa-solid fa-boxes-stacked"></i>
                  <p>Inventory</p>
                </NavLink>
              )}

              {hasFeature('Table Reservation') && (
                <NavLink to='/admin/admin-table' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                  <img src={assets.table_resrvation} alt="" style={{ filter: 'invert(100%)', width: '24px', height: '24px' }} />
                  <p>Tables</p>
                </NavLink>
              )}
              
              {hasFeature('Customer Database') && (
                <NavLink to='/admin/all-users' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                  <i className="fa-solid fa-users"></i>
                  <p>User Data</p>
                </NavLink>
              )}
              
              {hasFeature('Review Management') && (
                <NavLink to='/admin/all-reviews' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                  <i className="fa-regular fa-comments"></i>
                  <p>Reviews</p>
                </NavLink>
              )}
              
              {hasFeature('Point of Sale (POS)') && (
                <NavLink to='/admin/create-bill' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                  <i className="fa-solid fa-money-bill"></i>
                  <p>Create Bill</p>
                </NavLink>
              )}


              
              {hasFeature('Invoicing & GST Billing') && (
                <NavLink to='/admin/all-invoices' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                  <i className="fa-solid fa-file-invoice"></i>
                  <p>Invoices</p>
                </NavLink>
              )}

              {hasFeature('Kitchen Display (KDS)') && (
                <NavLink to='/admin/kitchen' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                  <i className="fa-solid fa-fire"></i>
                  <p>Kitchen View</p>
                </NavLink>
              )}

              {/* {hasFeature('AI Smart Prep Engine') && (
                <NavLink to='/admin/smart-prep' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                    <i className="fa-solid fa-robot"></i>
                    <p>Smart Prep AI</p>
                </NavLink>
              )} */}

              {hasFeature('Advanced Analytics') && (
                <NavLink to='/admin/graph' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                  <i className="fa-solid fa-chart-simple"></i>
                  <p>Graph</p>
                </NavLink>
              )}

              <NavLink to='/admin/settings' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                <i className="fa-solid fa-gear"></i>
                <p>Settings</p>
              </NavLink>
            </>
          )}

          {/* STAFF MANAGEMENT MODE */}
          {(userData?.role !== 'staff' || userData?.staffRole === 'manager') && adminMode === 'staff' && (
            <>
              {hasFeature('Staff Management') && (
                <NavLink to='/admin/team' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                  <i className="fa-solid fa-users-gear"></i>
                  <p>Team Members</p>
                </NavLink>
              )}

              {hasFeature('Attendance & Payroll') && (
                <>
                  <NavLink to='/admin/staff/attendance' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                    <i className="fa-solid fa-calendar-check"></i>
                    <p>Attendance</p>
                  </NavLink>
                  <NavLink to='/admin/staff/salary' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                    <i className="fa-solid fa-file-invoice-dollar"></i>
                    <p>Salary Slips</p>
                  </NavLink>
                  <NavLink to='/admin/staff/performance' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                    <i className="fa-solid fa-chart-line"></i>
                    <p>Performance</p>
                  </NavLink>
                </>
              )}
            </>
          )}

          {/* CHEF VIEW - Kitchen Only */}
          {userData?.role === 'staff' && userData?.staffRole !== 'manager' && (
            <>
              <NavLink to='/admin/kitchen' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                <i className="fa-solid fa-fire"></i>
                <p>Kitchen View</p>
              </NavLink>

              <NavLink to='/admin/staff-attendance' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                <i className="fa-solid fa-calendar-check" style={{ color: '#22c55e' }}></i>
                <p>Attendance</p>
              </NavLink>
              
              <NavLink to='/admin/chef-settings' className="tf-sidebar-option" onClick={() => {clickOption.play()}}>
                <i className="fa-solid fa-gear"></i>
                <p>Settings</p>
              </NavLink>
            </>
          )}

          {(JSON.parse(localStorage.getItem("userDetails"))?.role === 'owner' || localStorage.getItem('fromMaster') === 'true') && (
            <div 
              className="tf-sidebar-option" 
              onClick={() => {
                clickOption.play();
                const ownerToken = localStorage.getItem('ownerToken');
                if (ownerToken) {
                    window.location.href = `http://localhost:3001/dashboard?token=${ownerToken}`;
                } else {
                    window.location.href = `http://localhost:3001/dashboard`;
                }
              }}
              style={{ cursor: 'pointer', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: '0.5rem', paddingTop: '1rem' }}
            >
              <i className="fa-solid fa-shield-halved" style={{ color: 'var(--tf-primary)' }}></i>
              <p className="tf-master-text" style={{ color: 'var(--tf-primary)', fontWeight: '800' }}>Master Panel</p>
            </div>
          )}
        </div>
      </div>

      <div className="tf-sidebar-footer" style={{ marginTop: 'auto', padding: '1.5rem', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
        {(userData?.role !== 'staff' || userData?.staffRole === 'manager') && (
          <div className="tf-footer-profile" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem 1.25rem', marginBottom: '0.5rem' }}>
            {userData?.profilePhoto ? (
              <img 
                src={userData.profilePhoto} 
                alt="User" 
                style={{ width: '40px', height: '40px', borderRadius: '12px', objectFit: 'cover', border: '1px solid rgba(255,255,255,0.1)' }} 
              />
            ) : (
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '800' }}>
                {userData?.name?.charAt(0)}
              </div>
            )}
            <div className="tf-footer-text" style={{ overflow: 'hidden' }}>
              <div style={{ fontSize: '0.9rem', fontWeight: '800', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {userData?.name}
              </div>
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '1px' }}>
                {userData?.role}
              </div>
            </div>
          </div>
        )}

        <button 
          className="tf-sidebar-option logout-btn" 
          onClick={handleLogout}
          style={{ 
            width: '100%', 
            background: 'transparent', 
            border: 'none', 
            cursor: 'pointer',
            color: '#ff4135',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            padding: '0.85rem 1.25rem',
            transition: 'all 0.3s ease',
            borderRadius: 'var(--tf-radius-md)'
          }}
        >
          <i className="fa-solid fa-right-from-bracket" style={{ width: '24px', textAlign: 'center' }}></i>
          <p style={{ fontWeight: '700', margin: 0 }}>Logout</p>
        </button>
      </div>
    </div>
  )
}

export default Sidebar
